import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  getHsbQueryParam,
  getHtbQueryParam,
  getSessionTicketQueryParam,
} from "@/betby/adapters/router/queryParamsAdapter";
import {
  createAppConfigPersistStorage,
  PERSIST_STORAGE_NAMES,
} from "@/betby/store/persistStorage";

export type CurrencyMode = "gc" | "sc";

interface NavState {
  // 当前货币模式
  currentMode: CurrencyMode;
  // 余额
  balances: {
    gc: number;
    sc: number;
  };
  // 订单列表显示状态
  isOrderListVisible: boolean;
  // session_ticket
  session_ticket: string | null;
  // htb 参数（控制是否隐藏顶部导航）
  htb: string | null;
  // hsb 参数（控制是否隐藏页面滚动条）
  hsb: string | null;
  // 订单更新红点数量
  orderRedPointCount: number;
  // 切换货币模式
  toggleCurrency: () => void;
  // 设置货币模式
  setCurrency: (mode: CurrencyMode) => void;
  // 显示订单列表
  showOrderList: () => void;
  // 隐藏订单列表
  hideOrderList: () => void;
  // 更新余额
  updateBalance: (mode: CurrencyMode, amount: number) => void;
  // 增加余额
  addBalance: (mode: CurrencyMode, amount: number) => void;
  // 减少余额
  subtractBalance: (mode: CurrencyMode, amount: number) => void;
  // 设置 session_ticket
  setSessionTicket: (ticket: string | null) => void;
  // 设置 session_ticket 并标记强制登录 (批量更新防止多次渲染)
  setSessionTicketAndForceLogin: (
    ticket: string | null,
    needed: boolean,
  ) => void;
  // 设置 htb 参数
  setHtb: (htb: string | null) => void;
  // 设置 hsb 参数
  setHsb: (hsb: string | null) => void;
  // 设置订单红点数量
  setOrderRedPointCount: (count: number) => void;
  // 悬浮球位置
  orderIconPosition: { x: number; y: number } | null;
  // 设置悬浮球位置
  setOrderIconPosition: (position: { x: number; y: number }) => void;
  // 当前选中的订单列表 Tab
  activeOrderTab: "unsettled" | "settled";
  // 设置订单列表 Tab
  setActiveOrderTab: (tab: "unsettled" | "settled") => void;
  // 是否已完成 hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  // 是否需要强制登录
  forceLoginNeeded: boolean;
  setForceLoginNeeded: (needed: boolean) => void;
  // 初始化货币模式（不发送消息）
  initCurrency: (mode: CurrencyMode) => void;
}

type PersistedNavState = Pick<
  NavState,
  | "currentMode"
  | "balances"
  | "session_ticket"
  | "htb"
  | "hsb"
  | "orderRedPointCount"
  | "orderIconPosition"
>;

export const useNavStore = create<NavState>()(
  persist(
    (set) => ({
      currentMode: "gc",
      balances: {
        gc: 0.0,
        sc: 0.0,
      },
      isOrderListVisible: false,
      session_ticket: null,
      htb: null,
      hsb: null,
      orderRedPointCount: 0,
      orderIconPosition: null,
      activeOrderTab: "unsettled",
      _hasHydrated: false,
      forceLoginNeeded: false,

      toggleCurrency: () => {
        set((state) => {
          const nextMode = state.currentMode === "gc" ? "sc" : "gc";
          localStorage.setItem("winburst-mode", nextMode);
          return {
            currentMode: nextMode,
          };
        });
      },
      setCurrency: (mode) => {
        set({ currentMode: mode });
      },
      initCurrency: (mode) => {
        set({ currentMode: mode });
      },

      showOrderList: () => {
        set({ isOrderListVisible: true });
      },
      hideOrderList: () => {
        set({ isOrderListVisible: false });
      },
      updateBalance: (mode, amount) => {
        set((state) => ({
          balances: {
            ...state.balances,
            [mode]: amount,
          },
        }));
      },
      addBalance: (mode, amount) => {
        set((state) => ({
          balances: {
            ...state.balances,
            [mode]: state.balances[mode] + amount,
          },
        }));
      },
      subtractBalance: (mode, amount) => {
        set((state) => ({
          balances: {
            ...state.balances,
            [mode]: Math.max(0, state.balances[mode] - amount),
          },
        }));
      },
      setSessionTicket: (ticket) => {
        set((state) => {
          const next: Partial<NavState> = { session_ticket: ticket };
          // 切换账号时重置红点，避免新账号沿用旧账号的红点数量
          if (ticket !== state.session_ticket) {
            next.orderRedPointCount = 0;
          }
          return next;
        });
      },
      setSessionTicketAndForceLogin: (ticket, needed) => {
        set((state) => {
          const next: Partial<NavState> = {
            session_ticket: ticket,
            forceLoginNeeded: needed,
          };
          // 切换账号时重置红点，避免新账号沿用旧账号的红点数量
          if (ticket !== state.session_ticket) {
            next.orderRedPointCount = 0;
          }
          return next;
        });
      },
      setHtb: (htb) => {
        set({ htb });
      },
      setHsb: (hsb) => {
        set({ hsb });
      },
      setOrderRedPointCount: (count) => {
        set({ orderRedPointCount: count });
      },
      setOrderIconPosition: (position) => {
        set({ orderIconPosition: position });
      },
      setActiveOrderTab: (tab) => {
        set({ activeOrderTab: tab });
      },
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
      setForceLoginNeeded: (needed) => {
        set({ forceLoginNeeded: needed });
      },
    }),
    {
      name: PERSIST_STORAGE_NAMES.nav,
      storage: createAppConfigPersistStorage<PersistedNavState>("nav"),
      partialize: (state): PersistedNavState => ({
        currentMode: state.currentMode,
        balances: state.balances,
        session_ticket: state.session_ticket,
        htb: state.htb,
        hsb: state.hsb,
        orderRedPointCount: state.orderRedPointCount,
        orderIconPosition: state.orderIconPosition,
        // activeOrderTab 不需要持久化
        // _hasHydrated 不需要持久化
        // forceLoginNeeded 不需要持久化
      }),
      onRehydrateStorage: () => (state) => {
        // 在客户端数据恢复后，检查 URL 参数并更新
        // 优先级：URL 参数 > localStorage
        if (typeof window !== "undefined") {
          const ticketFromUrl = getSessionTicketQueryParam();
          if (
            ticketFromUrl &&
            state &&
            ticketFromUrl !== state.session_ticket
          ) {
            state.setSessionTicketAndForceLogin(ticketFromUrl, true);
          }

          const htbFromUrl = getHtbQueryParam();
          if (state && htbFromUrl) {
            state.setHtb(htbFromUrl);
          }

          const hsbFromUrl = getHsbQueryParam();
          if (state && hsbFromUrl) {
            state.setHsb(hsbFromUrl);
          }
        }
        state?.setHasHydrated(true);
      },
    },
  ),
);
